import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';

export interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  loading?: boolean;
  isAlert?: boolean; // When true, shows only OK button (no cancel)
}

export function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  loading = false,
  isAlert = false,
}: ConfirmationDialogProps) {
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    if (isAlert) {
      onClose(); // For alerts, close after action
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        {!isAlert && (
          <Button onClick={handleClose} disabled={loading}>
            {cancelText}
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={confirmColor}
          disabled={loading}
        >
          {isAlert ? 'OK' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Hook to manage confirmation dialog state
export function useConfirmationDialog() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [config, setConfig] = React.useState<Omit<ConfirmationDialogProps, 'open' | 'onClose' | 'onConfirm'>>({
    message: '',
  });
  const [onConfirmCallback, setOnConfirmCallback] = React.useState<(() => void) | null>(null);

  const openDialog = React.useCallback((
    message: string,
    onConfirm: () => void,
    options?: Partial<Omit<ConfirmationDialogProps, 'open' | 'onClose' | 'onConfirm' | 'message'>>
  ) => {
    setConfig({
      message,
      title: options?.title || 'Confirm Action',
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      confirmColor: options?.confirmColor || 'primary',
      loading: options?.loading || false,
      isAlert: options?.isAlert || false,
    });
    setOnConfirmCallback(() => onConfirm);
    setIsOpen(true);
  }, []);

  const openAlert = React.useCallback((
    message: string,
    options?: {
      title?: string;
      confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
      onClose?: () => void;
    }
  ) => {
    setConfig({
      message,
      title: options?.title || 'Information',
      confirmText: 'OK',
      confirmColor: options?.confirmColor || 'primary',
      isAlert: true,
      loading: false,
    });
    setOnConfirmCallback(options?.onClose ? () => options.onClose!() : null);
    setIsOpen(true);
  }, []);

  const closeDialog = React.useCallback(() => {
    setIsOpen(false);
    setOnConfirmCallback(null);
  }, []);

  const handleConfirm = React.useCallback(() => {
    if (onConfirmCallback) {
      onConfirmCallback();
    }
    if (!config.isAlert) {
      closeDialog();
    }
  }, [onConfirmCallback, closeDialog, config.isAlert]);

  const ConfirmationDialogComponent = React.useCallback(() => (
    <ConfirmationDialog
      open={isOpen}
      onClose={closeDialog}
      onConfirm={handleConfirm}
      {...config}
    />
  ), [isOpen, closeDialog, handleConfirm, config]);

  return {
    openDialog,
    openAlert,
    closeDialog,
    ConfirmationDialog: ConfirmationDialogComponent,
    isOpen,
  };
}